/**
 * TenantSwitcher Component — Super Admin Tenant Selection Dropdown
 *
 * Dropdown menu for super admins to switch between tenants or view global metrics.
 * Fetches tenant list from GET /admin/tenants with pagination and search.
 *
 * Features:
 * - Paginated list of all tenants (20 per page)
 * - Real-time search by tenant name
 * - "Visão Global" option to view aggregated metrics
 * - Loading and error states
 * - Only visible to super admins
 *
 * API Endpoint:
 * GET /api/v1/admin/tenants
 * Params: {page: 1, per_page: 20, search: '', status: 'active'}
 * Response: {data: Tenant[], meta: {total_count, current_page, total_pages, per_page}}
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Select, Spin, Empty } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTenantContext } from '@/context/TenantContext';
import { useAuth } from '@/context/AuthContext';
import Api from '@/services/api';

/**
 * Tenant interface from API
 */
interface Tenant {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  users_count?: number;
}

/**
 * TenantSwitcher component
 *
 * Props: None (uses context directly)
 * Returns: React component or null (if not super admin)
 *
 * Example:
 * ```tsx
 * <Header>
 *   <TenantSwitcher />
 * </Header>
 * ```
 */
export const TenantSwitcher: React.FC = () => {
  const { user } = useAuth();
  const { selectedTenantId, setSelectedTenantId, setGlobalView } = useTenantContext();
  const api = useMemo(() => new Api(), []);

  // Component state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);

  const isSuperAdmin = !!user?.is_superadmin;

  /**
   * Fetch tenants from API with pagination
   */
  const fetchTenants = useCallback(async (searchValue: string = '') => {
    try {
      setLoading(true);
      // Caminho relativo: a baseURL já inclui /api/v1, evitar prefixo duplicado
      const response = await api.get<any>('admin/tenants', {
        page: '1',
        per_page: '20',
        search: searchValue,
        status: 'active',
      });
      const responseData = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

      const normalizedTenants = responseData
        .map((tenant: Tenant | any) => {
          const numericId = Number(tenant?.id);
          if (!Number.isFinite(numericId)) return null;

          const usersCount =
            typeof tenant?.users_count === 'number'
              ? tenant.users_count
              : typeof tenant?.user_count === 'number'
                ? tenant.user_count
                : undefined;

          return {
            ...tenant,
            id: numericId,
            users_count: usersCount,
          } as Tenant;
        })
        .filter(Boolean) as Tenant[];

      setTenants(normalizedTenants);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * Load tenants on component mount
   */
  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
    }
  }, [fetchTenants, isSuperAdmin]);

  /**
   * Handle search input change
   */
  const handleSearch = (value: string) => {
    if (isSuperAdmin) {
      fetchTenants(value);
    }
  };

  /**
   * Handle tenant selection
   */
  const handleTenantSelect = (value: string | number) => {
    if (value === 'GLOBAL') {
      setGlobalView(true);
    } else {
      const tenantId = Number(value);
      
      if (!Number.isFinite(tenantId)) {
        return;
      }
      
      setSelectedTenantId(tenantId);
      setGlobalView(false);
    }
  };

  /**
   * Build Select options from tenant list
   */
  const options = [
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GlobalOutlined />
          <span>Visão Global</span>
        </div>
      ),
      value: 'GLOBAL',
    },
    ...tenants.map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    })),
  ];

  return (
    !isSuperAdmin ? null : (
    <Select
      placeholder="Selecionar tenant"
      value={selectedTenantId ?? 'GLOBAL'}
      onChange={handleTenantSelect}
      onSearch={handleSearch}
      showSearch
      filterOption={false}
      options={options}
      loading={loading}
      notFoundContent={
        loading ? <Spin size="small" /> : <Empty description="Nenhum tenant encontrado" />
      }
      style={{
        width: 200,
        minWidth: 180,
      }}
      popupMatchSelectWidth={false}
      allowClear={false}
    />
    )
  );
};

export default TenantSwitcher;
